import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Input, Button, Row, Col, Progress, Tag, Table, Typography, Space,
  Spin, message, Modal, Popconfirm, Descriptions, List, Divider, Empty, Collapse, Alert, Steps, Timeline,
} from 'antd';
import {
  SearchOutlined, DeleteOutlined, EyeOutlined, CheckCircleOutlined,
  CloseCircleOutlined, MinusCircleOutlined, ReloadOutlined,
  WarningOutlined, ThunderboltOutlined, RocketOutlined, TrophyOutlined,
  ToolOutlined, FileTextOutlined, CodeOutlined, BugOutlined, DollarOutlined,
  SafetyCertificateOutlined, RiseOutlined, AlertOutlined,
  DownloadOutlined, HistoryOutlined, GiftOutlined, SendOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { aeoScannerAPI, billingAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const gradeColors = { A: '#52c41a', B: '#1890ff', C: '#faad14', D: '#fa8c16', F: '#f5222d' };
const scoreColor = (s) => s >= 80 ? '#52c41a' : s >= 60 ? '#1890ff' : s >= 40 ? '#faad14' : '#f5222d';

const botStatusIcon = (status) => {
  if (status === 'allowed') return <Tag color="green" icon={<CheckCircleOutlined />}>允許</Tag>;
  if (status === 'blocked') return <Tag color="red" icon={<CloseCircleOutlined />}>封鎖</Tag>;
  return <Tag color="default" icon={<MinusCircleOutlined />}>未提及</Tag>;
};

// ── Grade-based action data from spec ──

const GRADE_CONFIG = {
  FD: {
    label: 'F / D 等級',
    color: '#f5222d',
    bg: '#fff1f0',
    border: '#ffa39e',
    icon: <AlertOutlined style={{ fontSize: 24, color: '#f5222d' }} />,
    title: '你的 AI 能見度處於危險區',
    subtitle: '分數 0–54｜AI 搜尋引擎幾乎看不到你',
    description: '你的網站目前對 ChatGPT、Perplexity、Gemini 等 AI 搜尋引擎幾乎是隱形的。這代表當潛在客戶問 AI「推薦哪家公司」時，你完全不會出現在回答中，而競爭對手卻可能正在被引用。',
    detail: 'F/D 等級的網站通常同時存在多個問題：AI 爬蟲被 robots.txt 封鎖、sitemap 連結失效、完全沒有結構化資料、也沒有任何 AI 可讀的內容說明文件。',
    cta: '立即行動：預約緊急 AI 診斷',
    ctaDesc: '你的 AI 能見度處於危險區，建議立即預約「品牌 AI 能見度緊急診斷」，由顧問團隊協助在 48 小時內完成全面健檢，找出所有封鎖點並提供修復清單。',
    services: [
      { emoji: '🔴', name: '品牌 AI 能見度健檢報告', desc: '全面診斷 AI 引用現況，含競品對比、雷達圖分析、行動建議清單', target: '所有 F/D 等級品牌' },
      { emoji: '🔧', name: 'GEO 技術能見度優化模組 B', desc: 'Schema 實作、robots.txt 修正、sitemap 重建、AI 爬蟲友善設定', target: '技術面全部缺項的網站' },
      { emoji: '📦', name: 'GEO 全套建置（A+B+C）', desc: '技術優化 + 品牌知識結構化 + 跨平台引用素材，完整 30 天建置', target: '預算充足、希望快速突破的品牌' },
    ],
    nextSteps: [
      '參考本頁「三大問題修復指南」，先處理最緊急的技術封鎖問題',
      '完成後重新掃描，確認分數是否提升',
      '聯絡專注玩星預約品牌 AI 能見度健檢報告，取得完整診斷',
    ],
  },
  C: {
    label: 'C 等級',
    color: '#faad14',
    bg: '#fffbe6',
    border: '#ffe58f',
    icon: <WarningOutlined style={{ fontSize: 24, color: '#faad14' }} />,
    title: '你有基礎但有明顯缺口',
    subtitle: '分數 55–69｜技術面部分到位，但 AI 仍難以完整理解你的品牌',
    description: '你的網站已經有基本的爬蟲友善設定，AI 爬蟲進得來，但進來之後「看不懂你在做什麼」。缺少結構化資料（JSON-LD）就像是店裡沒有招牌和菜單——客人進門了，但不知道賣什麼。',
    detail: 'C 等級的網站通常 robots.txt 和 sitemap 已存在，但缺少 JSON-LD Schema 和 llms.txt，導致內容品質分數偏低，AI 在回答問題時不會優先引用你的網站。',
    cta: '推薦行動：查看優化建議報告，針對缺口補強',
    ctaDesc: '建議取得「AI 搜尋優化建議報告」，針對你的品牌缺口提供具體內容建議，包含 JSON-LD 程式碼範本、llms.txt 撰寫模板、FAQ 題目清單。',
    priorityFixes: [
      { num: 1, name: '加入 JSON-LD Organization Schema', impact: '內容品質 +45分', difficulty: '低（複製貼上程式碼）' },
      { num: 2, name: '建立 llms.txt 並上傳根目錄', impact: '爬蟲友好 +30分', difficulty: '低（記事本即可撰寫）' },
      { num: 3, name: '新增 FAQPage Schema', impact: '內容品質 +25分', difficulty: '中（需有常見問題頁面）' },
    ],
    services: [
      { emoji: '📋', name: 'AI 搜尋優化建議報告', desc: '依掃描結果自動產生個人化建議，含程式碼範本與操作步驟', benefit: '分數預期提升 20–35 分' },
      { emoji: '🛠️', name: 'GEO 模組 A：品牌知識結構化', desc: '50 條 Q&A、3 篇長文、llms.txt、JSON-LD 全套建置', benefit: 'AI 引用率顯著提升' },
    ],
    nextSteps: [
      '立即修復 JSON-LD 和 llms.txt（參考下方修復指南）',
      '完成後重新掃描，確認分數提升到 B 級（70+）',
      '取得 AI 搜尋優化建議報告，進行深度內容優化',
    ],
  },
  B: {
    label: 'B 等級',
    color: '#1890ff',
    bg: '#e6f7ff',
    border: '#91d5ff',
    icon: <RocketOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
    title: '你表現不錯，是時候開始監測競品動態了',
    subtitle: '分數 70–84｜技術面紮實，但競品可能正在追趕',
    description: '恭喜！你的網站在 AI 搜尋友好度方面已達到良好水準。robots.txt、sitemap、結構化資料等技術基礎都已到位，AI 爬蟲可以正常存取並理解你的品牌。',
    detail: '然而，達到 B 級不代表可以停下來。AI 搜尋市場競爭激烈，競品可能正在加速優化。B 級品牌最應該關注的是：你在 AI 回答中的引用品質如何？競品被引用的頻率與情緒又是什麼？',
    cta: '推薦行動：開始監測競品，持續進攻',
    ctaDesc: '建議啟動「AI 引用監測計畫」，每月追蹤你與競品在 ChatGPT、Perplexity、Gemini 的引用頻率與情緒變化，及時發現市場動態。',
    advancedActions: [
      'FAQ Schema 優化：新增或強化 FAQPage Schema，讓 AI 在問答場景中更常引用你',
      '跨平台內容佈局：在 LinkedIn、Medium 等高權重平台發布品牌觀點文章',
      '競品關鍵字監測：分析競品在哪些問題類型被引用，找出你的內容缺口',
      'llms.txt 更新：定期更新 llms.txt，確保 AI 了解你最新的產品與服務',
      'E-E-A-T 信號強化：建立作者頁面、引用媒體報導、強化機構可信度信號',
    ],
    services: [
      { emoji: '📊', name: 'AI 引用監測月報', desc: '每月追蹤品牌與競品在各大 AI 平台的引用頻率、情緒、URL 排行', timing: '立即開始' },
      { emoji: '✍️', name: 'GEO 模組 C：跨平台引用素材', desc: '9 篇外部高權重平台文章、LinkedIn 專家文章、數據型內容', timing: '已完成技術優化後' },
      { emoji: '📋', name: 'AI 品牌能見度健檢報告', desc: '完整診斷你在 AI 引擎的引用現況，提供競品對照與缺口地圖', timing: '每季執行一次' },
    ],
    nextSteps: [
      '確認 FAQ Schema 是否已實作，未完成請參考下方修復指南',
      '聯絡專注玩星，啟動 AI 引用監測月報服務',
      '規劃一季跨平台內容佈局，建立外部引用信任度',
    ],
  },
  A: {
    label: 'A 等級',
    color: '#52c41a',
    bg: '#f6ffed',
    border: '#b7eb8f',
    icon: <TrophyOutlined style={{ fontSize: 24, color: '#52c41a' }} />,
    title: '維持優勢，加入引用監測計畫保持領先',
    subtitle: '分數 85–100｜AI 能見度優異，是時候鞏固市場地位',
    description: '你的網站在 AI 搜尋友好度方面表現優異，技術面、內容品質與 AI 能見度三個維度都達到高分。這代表你已經在 AI 搜尋時代建立了良好的基礎設施。',
    detail: 'A 級品牌面臨的挑戰不是「如何被看見」，而是「如何持續被選擇」。AI 引擎會根據內容新鮮度、引用頻率和情緒傾向動態調整推薦順序，今天的領先不代表明天的保證。',
    cta: '推薦行動：加入引用監測計畫，維持長期領先',
    ctaDesc: '建議加入「AI 品牌引用監測計畫」，持續追蹤品牌在各大 AI 平台的引用狀態，並在競品追趕時即時預警。',
    maintenanceActions: [
      '每月監測：持續追蹤品牌在 ChatGPT、Perplexity、Gemini 的引用頻率與情緒',
      '內容保鮮：每月發布 2–4 篇新內容，保持 AI 訓練資料的新鮮度',
      '競品預警：設定能見度驟降預警，競品有重大動作時立即收到通知',
      '引用 URL 分析：追蹤哪些頁面被 AI 引用最多，優化高引用頁面的內容深度',
      'llms.txt 季更新：每季審閱並更新 llms.txt，確保 AI 掌握最新品牌資訊',
      '知識圖譜建立：提交 Wikidata 品牌詞條，建立 AI 可識別的權威信號',
    ],
    services: [
      { emoji: '🏆', name: 'AI 引用監測月報（旗艦版）', desc: '含 URL 引用追蹤、競品對照、能見度預警、月度顧問諮詢', frequency: '每月' },
      { emoji: '📈', name: 'AI 品牌能見度健檢報告', desc: '完整 25–40 頁診斷報告，追蹤季度能見度變化趨勢', frequency: '每季' },
      { emoji: '✍️', name: '內容維護套件', desc: '每月 4 篇 AI 優化內容，維持高引用率', frequency: '每月' },
    ],
    nextSteps: [
      '聯絡專注玩星，啟動 AI 引用監測月報訂閱',
      '規劃下季的 AI 能見度健檢，確認競品是否有追趕跡象',
      '評估知識圖譜建立可行性（Wikidata 詞條提交）',
    ],
  },
};

function getGradeKey(grade, score) {
  if (grade === 'F' || grade === 'D' || score < 55) return 'FD';
  if (grade === 'C' || (score >= 55 && score < 70)) return 'C';
  if (grade === 'B' || (score >= 70 && score < 85)) return 'B';
  return 'A';
}

function detectProblems(cf, cq, av) {
  const problems = [];
  if (cf.bots) {
    const blocked = Object.entries(cf.bots).filter(([, s]) => s === 'blocked');
    if (blocked.length > 0)
      problems.push({ icon: <CloseCircleOutlined style={{ color: '#f5222d' }} />, label: 'AI 爬蟲狀態', text: `${blocked.map(([b]) => b).join('、')} 遭到封鎖，AI 搜尋引擎無法存取你的網站` });
  }
  if (!cq.has_json_ld)
    problems.push({ icon: <CodeOutlined style={{ color: '#f5222d' }} />, label: '結構化資料', text: '首頁未偵測到任何 JSON-LD Schema，AI 無法理解你的品牌與產品' });
  if (!cf.sitemap_exists)
    problems.push({ icon: <BugOutlined style={{ color: '#f5222d' }} />, label: '網站地圖', text: 'Sitemap 不存在或連結失效，AI 爬蟲無法得知網站有哪些頁面' });
  if (!cf.llms_txt_exists)
    problems.push({ icon: <FileTextOutlined style={{ color: '#fa8c16' }} />, label: 'llms.txt', text: '缺少 AI 可讀的內容說明文件，競爭對手若有則佔有先機' });
  if (!cq.has_faq_schema)
    problems.push({ icon: <MinusCircleOutlined style={{ color: '#fa8c16' }} />, label: 'FAQ Schema', text: '沒有 FAQPage Schema，AI 無法直接引用你的常見問題回答' });
  if (!cq.has_meta_description)
    problems.push({ icon: <MinusCircleOutlined style={{ color: '#fa8c16' }} />, label: 'Meta Description', text: '缺少 meta description，AI 在引用時無法快速理解頁面摘要' });
  return problems;
}

// ── Repair guides (static content from spec) ──

function RepairGuides({ cf, cq, domain }) {
  const d = domain || '你的網域';
  const needsSitemap = !cf.sitemap_exists;
  const needsJsonLd = !cq.has_json_ld;
  const needsLlms = !cf.llms_txt_exists;
  const defaultOpen = [
    ...(needsSitemap ? ['sitemap'] : []),
    ...(needsJsonLd ? ['jsonld'] : []),
    ...(needsLlms ? ['llms'] : []),
  ];

  return (
    <Card
      title={<span><ToolOutlined /> 三大問題修復指南</span>}
      style={{ marginBottom: 24 }}
      extra={<Text type="secondary">依掃描結果自動標示需修復項目</Text>}
    >
      <Collapse defaultActiveKey={defaultOpen} ghost>
        {/* Repair 1: Sitemap */}
        <Panel
          header={<span>{needsSitemap ? <Tag color="red">需修復</Tag> : <Tag color="green">已通過</Tag>} <strong>修復一：Sitemap 連結壞掉</strong> <Text type="secondary">（修復後 +30–40 分）</Text></span>}
          key="sitemap"
        >
          <Alert type="warning" showIcon style={{ marginBottom: 16 }}
            message="Sitemap 壞掉是最常見的低分原因之一，修復後爬蟲友好分數可提升 +30 分。" />
          <Paragraph>robots.txt 裡面有標記 sitemap 的位置，但實際去那個網址抓回傳 404（找不到）。搜尋引擎和 AI 爬蟲找不到網站地圖，就不知道網站有哪些頁面，導致部分頁面永遠不被索引。</Paragraph>
          <Steps direction="vertical" size="small" current={-1} items={[
            {
              title: '確認目前的 robots.txt 內容',
              description: <div>
                <Paragraph>用瀏覽器打開你的 robots.txt，找到 Sitemap 那一行，記下實際寫的路徑：</Paragraph>
                <pre style={codeStyle}>{`https://${d}/robots.txt\n# 找到類似這樣的行：\nSitemap: https://${d}/sitemap.xml`}</pre>
              </div>,
            },
            {
              title: '重新產生 sitemap.xml',
              description: <div>
                <Paragraph>依據網站使用的系統選擇對應方式：</Paragraph>
                <ul>
                  <li><strong>WordPress</strong> → 安裝 Yoast SEO 或 Rank Math 外掛，會自動產生並維護 sitemap</li>
                  <li><strong>91APP</strong> → sitemap 由平台自動產生，通常在 /sitemap.xml 或 /sitemap_index.xml</li>
                  <li><strong>自建網站</strong> → 使用 xml-sitemaps.com 免費產生，下載後上傳到網站根目錄</li>
                </ul>
              </div>,
            },
            {
              title: '更新 robots.txt — 確保 Sitemap 路徑正確',
              description: <div>
                <Paragraph>確保 robots.txt 最後一行有 Sitemap 宣告（路徑要跟實際 sitemap 位置一致）：</Paragraph>
                <pre style={codeStyle}>{`Sitemap: https://${d}/sitemap.xml`}</pre>
              </div>,
            },
            {
              title: '同時確認 AI 爬蟲沒被封鎖',
              description: <div>
                <Paragraph>建議改成明確允許主要 AI 爬蟲的完整設定：</Paragraph>
                <pre style={codeStyle}>{`User-agent: GPTBot\nAllow: /\n\nUser-agent: ClaudeBot\nAllow: /\n\nUser-agent: anthropic-ai\nAllow: /\n\nUser-agent: PerplexityBot\nAllow: /\n\nUser-agent: *\nAllow: /\n\nSitemap: https://${d}/sitemap.xml`}</pre>
              </div>,
            },
          ]} />
          <Divider />
          <Row gutter={[16, 8]}>
            <Col span={6}><Text strong>WordPress</Text></Col><Col span={18}><Text>Yoast SEO → SEO → 一般 → 功能 → XML Sitemap 開啟</Text></Col>
            <Col span={6}><Text strong>91APP</Text></Col><Col span={18}><Text>後台 → SEO 設定 → Sitemap 設定，確認路徑正確</Text></Col>
            <Col span={6}><Text strong>cPanel 主機</Text></Col><Col span={18}><Text>檔案管理員 → 找到 robots.txt 編輯</Text></Col>
            <Col span={6}><Text strong>Cloudflare</Text></Col><Col span={18}><Text>確認沒有阻擋爬蟲的安全規則</Text></Col>
          </Row>
        </Panel>

        {/* Repair 2: JSON-LD */}
        <Panel
          header={<span>{needsJsonLd ? <Tag color="red">需修復</Tag> : <Tag color="green">已通過</Tag>} <strong>修復二：沒有 JSON-LD 結構化資料</strong> <Text type="secondary">（加入後 +45 分）</Text></span>}
          key="jsonld"
        >
          <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message="加入 JSON-LD Organization Schema 後，內容品質分數可立即提升 +45 分。這是單一修復效果最大的一項。" />
          <Paragraph>AI 搜尋引擎看到沒有結構化資料的網站，只知道「有一堆文字」，但不知道這是什麼公司、賣什麼產品、信任度如何。JSON-LD 是藏在網頁原始碼裡的說明書，讓 AI 能正確理解並引用你的內容。</Paragraph>
          <Steps direction="vertical" size="small" current={-1} items={[
            {
              title: '在首頁 <head> 加入 Organization Schema（必做）',
              description: <div>
                <Paragraph>這段告訴 AI 這是什麼公司，放在首頁的 {'<head>'} 標籤裡：</Paragraph>
                <pre style={codeStyle}>{`<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "你的公司名稱",
  "url": "https://${d}",
  "logo": "https://${d}/logo.png",
  "description": "公司簡介（2-3句，說明主要業務）",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+886-2-XXXX-XXXX",
    "contactType": "customer service"
  }
}
</script>`}</pre>
              </div>,
            },
            {
              title: '產品頁加入 Product Schema（電商網站必做）',
              description: <div>
                <Paragraph>每個產品頁面加上這段，AI 才能回答「哪裡可以買到 XX 產品」：</Paragraph>
                <pre style={codeStyle}>{`<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "Product",
  "name": "產品名稱",
  "description": "產品描述",
  "image": "產品圖片網址",
  "brand": { "@type": "Brand", "name": "品牌名稱" },
  "offers": {
    "@type": "Offer",
    "price": "價格",
    "priceCurrency": "TWD",
    "availability": "https://schema.org/InStock"
  }
}
</script>`}</pre>
              </div>,
            },
            {
              title: '常見問題頁加入 FAQPage Schema（高效加分項）',
              description: <div>
                <Paragraph>FAQ Schema 是讓 AI 直接引用回答問題最有效的方式：</Paragraph>
                <pre style={codeStyle}>{`<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "常見問題一？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "完整的答案內容，建議 100 字以上"
      }
    }
  ]
}
</script>`}</pre>
              </div>,
            },
            {
              title: '驗證是否正確',
              description: <Paragraph>加完後到 Google 結構化資料測試工具確認，貼上網址，沒有紅色錯誤就是正確：<br /><Text copyable>https://search.google.com/test/rich-results</Text></Paragraph>,
            },
          ]} />
          <Divider />
          <Alert type="success" showIcon style={{ marginBottom: 8 }}
            message="WordPress 用戶安裝 Rank Math 或 Schema Pro 外掛，填表單就能自動產生，完全不需要寫程式碼。" />
          <Alert type="warning" showIcon
            message="JSON-LD 要放在 <head> 裡，不要放在 <body> 最底部，否則部分 AI 爬蟲可能讀不到。" />
        </Panel>

        {/* Repair 3: llms.txt */}
        <Panel
          header={<span>{needsLlms ? <Tag color="red">需修復</Tag> : <Tag color="green">已通過</Tag>} <strong>修復三：沒有 llms.txt</strong> <Text type="secondary">（建立後 +30 分 bonus）</Text></span>}
          key="llms"
        >
          <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message="llms.txt 不需要工程師，任何人用記事本就能撰寫，是三個修復項目中最容易完成的一個。目前台灣幾乎沒有網站有做，搶先完成是明顯的競爭優勢。" />
          <Paragraph>llms.txt 是 2024 年出現的新標準，讓 ChatGPT、Claude、Perplexity 等大型語言模型知道這個網站的內容摘要、重要頁面清單、使用限制。類似 robots.txt 之於搜尋引擎，llms.txt 是專為 AI 設計的說明文件。</Paragraph>
          <Steps direction="vertical" size="small" current={-1} items={[
            {
              title: '建立 llms.txt 檔案',
              description: <div>
                <Paragraph>用記事本新增 llms.txt，按照以下模板填入真實資訊：</Paragraph>
                <pre style={codeStyle}>{`# 公司名稱
# 英文名稱（若有）

> 一句話介紹：說明主要業務和目標客群

## 關於我們
- 公司成立年份
- 主要產品或服務
- 服務地區

## 主要頁面
- [首頁](https://${d}/)
- [產品目錄](https://${d}/products/)
- [關於我們](https://${d}/about/)
- [聯絡我們](https://${d}/contact/)

## 產品與服務
- 產品類別一：簡短說明
- 產品類別二：簡短說明

## 聯絡資訊
- 電話：+886-X-XXXX-XXXX
- Email：info@${d}

## 常見問題
- Q：最常被問到的問題一？A：答案
- Q：最常被問到的問題二？A：答案

## 內容授權聲明
AI 系統可以引用本網站內容用於回答使用者問題，引用時請標注來源網址。`}</pre>
              </div>,
            },
            {
              title: '上傳到網站根目錄',
              description: <div>
                <Paragraph>把 llms.txt 上傳到網站的最頂層資料夾（和 robots.txt 同一個位置），確認可以打開：</Paragraph>
                <pre style={codeStyle}>{`https://${d}/llms.txt`}</pre>
                <Row gutter={[16, 8]} style={{ marginTop: 12 }}>
                  <Col span={8}><Text strong>WordPress</Text></Col><Col span={16}><Text>主機控制台 → 檔案管理員 → public_html → 新增 llms.txt</Text></Col>
                  <Col span={8}><Text strong>91APP</Text></Col><Col span={16}><Text>後台新增頁面，固定連結設為 llms</Text></Col>
                  <Col span={8}><Text strong>自建網站</Text></Col><Col span={16}><Text>直接上傳到伺服器根目錄（與 index.html 同層）</Text></Col>
                  <Col span={8}><Text strong>Vercel / Netlify</Text></Col><Col span={16}><Text>將 llms.txt 放在 public/ 資料夾</Text></Col>
                </Row>
              </div>,
            },
            {
              title: '在 robots.txt 加上標記（選用）',
              description: <div>
                <Paragraph>可以在 robots.txt 最後加一行：</Paragraph>
                <pre style={codeStyle}>{`LLMs: https://${d}/llms.txt`}</pre>
              </div>,
            },
          ]} />
          <Divider />
          <Title level={5}>撰寫注意事項</Title>
          <ul>
            <li>內容要準確描述公司業務，不要複製貼上行銷話術</li>
            <li>llms.txt 是公開可讀的，不要放任何機密資訊、密碼或個人隱私</li>
            <li>當公司有新產品或服務時，記得同步更新 llms.txt</li>
            <li>建議同時準備繁體中文版（llms.txt）和英文版（llms-en.txt）</li>
            <li>字數建議 300–1000 字，太短資訊不足，太長 AI 可能不完整讀取</li>
          </ul>
        </Panel>
      </Collapse>
    </Card>
  );
}

const codeStyle = {
  background: '#f5f5f5', padding: '12px 16px', borderRadius: 6,
  fontSize: 13, lineHeight: 1.6, overflowX: 'auto', whiteSpace: 'pre-wrap',
  border: '1px solid #e8e8e8',
};

// ── Grade Action Panel ──

function GradeActionPanel({ grade, score, cf, cq, av }) {
  const key = getGradeKey(grade, score);
  const cfg = GRADE_CONFIG[key];
  const problems = detectProblems(cf, cq, av);

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Main grade card */}
      <Card
        style={{ borderColor: cfg.border, borderWidth: 2, marginBottom: 16 }}
        styles={{ header: { background: cfg.bg, borderBottom: `2px solid ${cfg.border}` } }}
        title={<Space>{cfg.icon}<span style={{ color: cfg.color, fontWeight: 700, fontSize: 18 }}>{cfg.title}</span></Space>}
        extra={<Tag color={cfg.color} style={{ fontSize: 14, padding: '4px 12px', fontWeight: 700 }}>{cfg.label}</Tag>}
      >
        <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 8 }}>{cfg.subtitle}</Paragraph>
        <Paragraph>{cfg.description}</Paragraph>
        <Paragraph type="secondary">{cfg.detail}</Paragraph>

        {/* CTA */}
        <div style={{
          background: cfg.bg, padding: '16px 20px', borderRadius: 8,
          border: `1px solid ${cfg.border}`, marginTop: 16, marginBottom: 16,
        }}>
          <Text strong style={{ fontSize: 15 }}>{cfg.cta}</Text>
          <Paragraph style={{ margin: '8px 0 0' }}>{cfg.ctaDesc}</Paragraph>
        </div>

        {/* Detected problems */}
        {problems.length > 0 && (
          <>
            <Divider orientation="left">本次掃描發現的主要問題</Divider>
            <Descriptions column={1} size="small" bordered>
              {problems.map((p, i) => (
                <Descriptions.Item key={i} label={<Space>{p.icon}{p.label}</Space>}>
                  {p.text}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </>
        )}

        {/* Priority fixes (C grade) */}
        {cfg.priorityFixes && (
          <>
            <Divider orientation="left">優先修復清單（按影響力排序）</Divider>
            <Table
              dataSource={cfg.priorityFixes}
              rowKey="num"
              size="small"
              pagination={false}
              columns={[
                { title: '#', dataIndex: 'num', width: 40 },
                { title: '修復項目', dataIndex: 'name', render: (t) => <Text strong>{t}</Text> },
                { title: '預期影響', dataIndex: 'impact', render: (t) => <Tag color="blue">{t}</Tag> },
                { title: '修復難度', dataIndex: 'difficulty' },
              ]}
            />
          </>
        )}

        {/* Advanced actions (B grade) */}
        {cfg.advancedActions && (
          <>
            <Divider orientation="left">B 級品牌的進階行動</Divider>
            <List
              size="small"
              dataSource={cfg.advancedActions}
              renderItem={(item) => (
                <List.Item><Text><RiseOutlined style={{ color: '#1890ff', marginRight: 8 }} />{item}</Text></List.Item>
              )}
            />
          </>
        )}

        {/* Maintenance actions (A grade) */}
        {cfg.maintenanceActions && (
          <>
            <Divider orientation="left">A 級品牌的維護策略</Divider>
            <List
              size="small"
              dataSource={cfg.maintenanceActions}
              renderItem={(item) => (
                <List.Item><Text><SafetyCertificateOutlined style={{ color: '#52c41a', marginRight: 8 }} />{item}</Text></List.Item>
              )}
            />
          </>
        )}
      </Card>

      {/* Service recommendations */}
      <Card title={<span><ThunderboltOutlined /> 建議服務方案</span>} style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          {cfg.services.map((svc, i) => (
            <Col xs={24} md={cfg.services.length <= 2 ? 12 : 8} key={i}>
              <Card size="small" hoverable style={{ height: '100%' }}>
                <div style={{ fontSize: 20, marginBottom: 8 }}>{svc.emoji}</div>
                <Text strong style={{ fontSize: 14 }}>{svc.name}</Text>
                <Paragraph type="secondary" style={{ fontSize: 13, margin: '8px 0 4px' }}>{svc.desc}</Paragraph>
                {svc.target && <Tag>{svc.target}</Tag>}
                {svc.benefit && <Tag color="blue">{svc.benefit}</Tag>}
                {svc.timing && <Tag color="cyan">{svc.timing}</Tag>}
                {svc.frequency && <Tag color="purple">{svc.frequency}</Tag>}
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Next steps */}
      <Card title={<span><RocketOutlined /> 下一步</span>} size="small" style={{ marginBottom: 16 }}>
        <Steps direction="vertical" size="small" current={-1}
          items={cfg.nextSteps.map((step) => ({ title: step }))}
        />
      </Card>
    </div>
  );
}

// ── Domain Score History ──

function DomainHistory({ domain }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!domain) return;
    setLoading(true);
    aeoScannerAPI.domainHistory(domain)
      .then(res => setHistory(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [domain]);

  if (loading) return <Card size="small" loading style={{ marginBottom: 24 }} />;
  if (history.length < 2) return null;

  return (
    <Card
      title={<span><HistoryOutlined /> {domain} 掃描歷史趨勢</span>}
      size="small"
      style={{ marginBottom: 24 }}
    >
      <Timeline
        items={history.map((h) => ({
          color: h.overall_score >= 85 ? 'green' : h.overall_score >= 70 ? 'blue' : h.overall_score >= 55 ? 'orange' : 'red',
          children: (
            <Space>
              <Text type="secondary">{new Date(h.created_at).toLocaleDateString('zh-TW')}</Text>
              <Tag color={gradeColors[h.grade]} style={{ fontWeight: 700 }}>{h.grade}</Tag>
              <Text strong style={{ color: scoreColor(h.overall_score), fontSize: 16 }}>{h.overall_score} 分</Text>
              {history.indexOf(h) > 0 && (() => {
                const prev = history[history.indexOf(h) - 1];
                const diff = h.overall_score - prev.overall_score;
                if (diff > 0) return <Tag color="green">+{diff}</Tag>;
                if (diff < 0) return <Tag color="red">{diff}</Tag>;
                return <Tag>±0</Tag>;
              })()}
            </Space>
          ),
        }))}
      />
    </Card>
  );
}


// ── Expected score improvement table ──

function ScoreImprovementTable({ cf, cq }) {
  const items = [];
  if (!cf.sitemap_exists)
    items.push({ fix: 'Sitemap 連結修復 + AI 爬蟲開放', gain: '+30–40 分（爬蟲友好）', note: 'robots.txt 和 sitemap 都會顯示「存在」' });
  if (!cq.has_json_ld)
    items.push({ fix: 'JSON-LD Organization Schema 加入', gain: '+45 分（內容品質）', note: 'JSON-LD 結構化資料顯示「存在」' });
  if (!cq.has_faq_schema)
    items.push({ fix: 'FAQPage Schema 加入', gain: '+25 分（內容品質）', note: 'FAQ Schema 顯示「存在」' });
  if (!cf.llms_txt_exists)
    items.push({ fix: 'llms.txt 建立上傳', gain: '+30 分（爬蟲友好）', note: '必要檔案欄顯示 llms.txt「存在」' });

  if (items.length === 0) return null;

  return (
    <Card title="重新掃描 × 預期效果" size="small" style={{ marginBottom: 24 }}>
      <Table
        dataSource={items}
        rowKey="fix"
        size="small"
        pagination={false}
        columns={[
          { title: '修復項目', dataIndex: 'fix', render: (t) => <Text><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} /> {t}</Text> },
          { title: '預期加分', dataIndex: 'gain', render: (t) => <Tag color="blue">{t}</Tag> },
          { title: '確認方式', dataIndex: 'note', render: (t) => <Text type="secondary">{t}</Text> },
        ]}
      />
      <div style={{ marginTop: 12, textAlign: 'center' }}>
        <Tag color="gold" style={{ fontSize: 14, padding: '4px 16px' }}>
          🎯 全部完成後預期：F→C ｜ C→B ｜ B→A（通常提升 30–60 分）
        </Tag>
      </div>
    </Card>
  );
}

// ══════════════════════════════════════════════════════
// Main Page
// ══════════════════════════════════════════════════════

export default function AeoScannerPage() {
  const [url, setUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [currentScan, setCurrentScan] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // null=loading, true=active, false=inactive
  const [subGateOpen, setSubGateOpen] = useState(false);
  const navigate = useNavigate();

  // Check subscription on load
  useEffect(() => {
    (async () => {
      try {
        const res = await billingAPI.getStatus();
        const active = res.data?.ai_seo_subscription_active === true;
        setSubscriptionStatus(active);
      } catch {
        setSubscriptionStatus(false);
      }
    })();
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await aeoScannerAPI.list();
      setHistory(res.data || []);
    } catch (e) { /* ignore */ }
    setLoadingHistory(false);
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleScan = async () => {
    if (!url.trim()) return message.warning('請輸入網址');
    if (!subscriptionStatus) {
      setSubGateOpen(true);
      return;
    }
    setScanning(true);
    setCurrentScan(null);
    try {
      const res = await aeoScannerAPI.scan({ url: url.trim() });
      const result = res.data;
      setCurrentScan(result);
      if (result.status === 'failed') {
        message.error(`掃描失敗：${result.error_message || '未知錯誤'}`);
      } else {
        message.success(`掃描完成！評級 ${result.grade}，總分 ${result.overall_score}`);
      }
      fetchHistory();
    } catch (e) {
      message.error('掃描請求失敗：' + (e.message || '網路錯誤'));
    }
    setScanning(false);
  };

  const handleView = async (id) => {
    try {
      const res = await aeoScannerAPI.get(id);
      setCurrentScan(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { message.error('載入失敗'); }
  };

  const handleDelete = async (id) => {
    try {
      await aeoScannerAPI.delete(id);
      message.success('已刪除');
      if (currentScan?.id === id) setCurrentScan(null);
      fetchHistory();
    } catch (e) { message.error('刪除失敗'); }
  };

  const a = currentScan?.analysis_result;
  const cf = a?.crawl_friendliness || {};
  const cq = a?.content_quality || {};
  const av = a?.ai_visibility || {};

  const historyColumns = [
    { title: '網域', dataIndex: 'domain', key: 'domain', render: (d) => <Text strong>{d}</Text> },
    {
      title: '評分', dataIndex: 'overall_score', key: 'score', width: 80,
      render: (s, r) => r.status === 'completed' ? <Text strong style={{ color: scoreColor(s) }}>{s}</Text> : <Tag color="orange">{r.status}</Tag>,
    },
    {
      title: '評級', dataIndex: 'grade', key: 'grade', width: 60,
      render: (g) => g ? <Tag color={gradeColors[g]} style={{ fontWeight: 700, fontSize: 16 }}>{g}</Tag> : '-',
    },
    {
      title: '時間', dataIndex: 'created_at', key: 'time', width: 170,
      render: (t) => t ? new Date(t).toLocaleString('zh-TW') : '-',
    },
    {
      title: '操作', key: 'action', width: 180,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); handleView(r.id); }} />
          {r.status === 'completed' && (
            <Button
              size="small"
              type="primary"
              icon={<ToolOutlined />}
              onClick={(e) => { e.stopPropagation(); navigate(`/aeo-fix?scan_id=${r.id}`); }}
            >
              修復
            </Button>
          )}
          <Popconfirm title="確定刪除？" onConfirm={(e) => { e?.stopPropagation?.(); handleDelete(r.id); }}>
            <Button size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} />
          </Popconfirm>
        </Space>
      ),
    },
  ];
  return (
    <div style={{ padding: "24px", maxWidth: 1200, margin: "0 auto" }}>

      <Paragraph type="secondary">
        輸入任何網站網址，自動偵測 robots.txt、sitemap、llms.txt、JSON-LD 結構化資料，AI 分析並評分，提供完整行動建議。
      </Paragraph>
      {/* Scan Input */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={12}>
          <Col flex="auto">
            <Input
              size="large"
              placeholder="輸入網址，例如 https://www.example.com"
              prefix={<SearchOutlined />}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onPressEnter={handleScan}
              disabled={scanning}
            />
          </Col>
          <Col>
            <Button type="primary" size="large" onClick={handleScan} loading={scanning}
              icon={<SearchOutlined />}>
              {scanning ? '掃描中...' : '開始掃描'}
            </Button>
          </Col>
        </Row>
        {scanning && (
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: '#999' }}>
              正在抓取網站資料並進行 AI 分析，約需 15-30 秒...
            </div>
          </div>
        )}
      </Card>

      {/* Results */}
      {currentScan && currentScan.status === 'completed' && a && (
        <>
          {/* Score Cards */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Card hoverable style={{ textAlign: 'center' }}>
                <Progress type="circle" percent={a.overall_score} size={100}
                  strokeColor={scoreColor(a.overall_score)}
                  format={() => <span style={{ fontSize: 28, fontWeight: 800 }}>{a.overall_score}</span>} />
                <div style={{ marginTop: 12 }}>
                  <Tag color={gradeColors[a.grade]} style={{ fontSize: 20, fontWeight: 800, padding: '4px 16px' }}>{a.grade} 級</Tag>
                </div>
                <div style={{ marginTop: 8, color: '#666', fontSize: 13 }}>總分</div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card hoverable style={{ textAlign: 'center' }}>
                <Progress type="circle" percent={cf.score || 0} size={80}
                  strokeColor={scoreColor(cf.score || 0)}
                  format={() => cf.score ?? '-'} />
                <div style={{ marginTop: 12, fontWeight: 600 }}>爬蟲友好度</div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card hoverable style={{ textAlign: 'center' }}>
                <Progress type="circle" percent={cq.score || 0} size={80}
                  strokeColor={scoreColor(cq.score || 0)}
                  format={() => cq.score ?? '-'} />
                <div style={{ marginTop: 12, fontWeight: 600 }}>內容品質</div>
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card hoverable style={{ textAlign: 'center' }}>
                <Progress type="circle" percent={av.score || 0} size={80}
                  strokeColor={scoreColor(av.score || 0)}
                  format={() => av.score ?? '-'} />
                <div style={{ marginTop: 12, fontWeight: 600 }}>AI 能見度</div>
              </Card>
            </Col>
          </Row>

          {/* Details */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} md={12}>
              <Card title="AI 爬蟲權限" size="small">
                <Descriptions column={1} size="small" bordered>
                  {cf.bots && Object.entries(cf.bots).map(([bot, status]) => (
                    <Descriptions.Item key={bot} label={<Text code>{bot}</Text>}>
                      {botStatusIcon(status)}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
                <div style={{ marginTop: 12 }}>
                  <Space wrap>
                    <Tag color={cf.robots_txt_exists ? 'green' : 'red'}>
                      robots.txt {cf.robots_txt_exists ? '存在' : '未找到'}
                    </Tag>
                    <Tag color={cf.sitemap_exists ? 'green' : 'red'}>
                      sitemap.xml {cf.sitemap_exists ? '存在' : '未找到'}
                    </Tag>
                    <Tag color={cf.llms_txt_exists ? 'green' : 'red'}>
                      llms.txt {cf.llms_txt_exists ? '存在' : '未找到'}
                    </Tag>
                  </Space>
                </div>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="內容品質偵測" size="small">
                <Descriptions column={1} size="small" bordered>
                  <Descriptions.Item label="JSON-LD 結構化資料">
                    {cq.has_json_ld
                      ? <><Tag color="green">已偵測</Tag> {(cq.json_ld_types || []).map(t => <Tag key={t}>{t}</Tag>)}</>
                      : <Tag color="red">未偵測到</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="FAQ Schema">
                    {cq.has_faq_schema ? <Tag color="green">有</Tag> : <Tag color="default">無</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Meta Description">
                    {cq.has_meta_description ? <Tag color="green">有</Tag> : <Tag color="red">無</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Open Graph">
                    {cq.has_open_graph ? <Tag color="green">有</Tag> : <Tag color="default">無</Tag>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Canonical URL">
                    {cq.has_canonical ? <Tag color="green">有</Tag> : <Tag color="default">無</Tag>}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          </Row>

          {/* AI Visibility + Summary */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} md={12}>
              <Card title="AI 能見度評估" size="small">
                <Paragraph>{av.assessment || '-'}</Paragraph>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="總結" size="small">
                <Paragraph>{a.summary_zh || '-'}</Paragraph>
              </Card>
            </Col>
          </Row>

          {/* AI Recommendations */}
          <Card title="AI 優化建議" style={{ marginBottom: 24 }}>
            <List
              dataSource={a.recommendations_zh || []}
              renderItem={(item, idx) => (
                <List.Item>
                  <Text><Tag color="blue">{idx + 1}</Tag> {item}</Text>
                </List.Item>
              )}
            />
          </Card>

          {/* ════════════════════════════════════════════ */}
          {/* ACTION RECOMMENDATIONS (from spec) */}
          {/* ════════════════════════════════════════════ */}

          <Divider style={{ fontSize: 18, fontWeight: 700 }}>
            <ThunderboltOutlined /> 掃描後行動建議
          </Divider>

          {/* Grade-based action panel */}
          <GradeActionPanel grade={a.grade} score={a.overall_score} cf={cf} cq={cq} av={av} />

          {/* Repair guides */}
          <RepairGuides cf={cf} cq={cq} domain={currentScan.domain} />

          {/* Expected score improvement */}
          <ScoreImprovementTable cf={cf} cq={cq} />

          {/* Domain history */}
          <DomainHistory domain={currentScan.domain} />

          {/* AI Auto-Fix CTA */}
          <Card
            size="small"
            style={{ marginBottom: 24, borderColor: '#1890ff', borderWidth: 2 }}
            styles={{ body: { background: '#e6f7ff' } }}
          >
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  <ToolOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                  <div>
                    <div style={{ fontWeight: 700, color: '#1890ff' }}>AI 自動修復可用</div>
                    <div style={{ fontSize: 13, color: '#666' }}>一鍵透過 SSH 讓 AI 自動修復 robots.txt、sitemap、llms.txt、JSON-LD 等問題</div>
                  </div>
                </Space>
              </Col>
              <Col>
                <Button
                  type="primary"
                  size="large"
                  icon={<ToolOutlined />}
                  onClick={() => navigate(`/aeo-fix?scan_id=${currentScan.id}`)}
                >
                  立即修復問題
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Meta + Export */}
          <Card size="small" style={{ marginBottom: 24 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Space split={<Divider type="vertical" />} wrap>
                  <Text type="secondary">網域：{currentScan.domain}</Text>
                  <Text type="secondary">模型：{currentScan.ai_model_used}</Text>
                  <Text type="secondary">耗時：{currentScan.latency_ms ? `${(currentScan.latency_ms / 1000).toFixed(1)}s` : '-'}</Text>
                  <Text type="secondary">Token：{currentScan.tokens_used || '-'}</Text>
                </Space>
              </Col>
              <Col>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={async () => {
                    try {
                      message.loading({ content: '正在產生 PDF 報告...', key: 'pdf' });
                      const blob = await aeoScannerAPI.exportReport(currentScan.id);
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `AEO_Report_${currentScan.domain}.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                      message.success({ content: 'PDF 報告已下載', key: 'pdf' });
                    } catch (e) {
                      message.error({ content: '匯出失敗', key: 'pdf' });
                    }
                  }}
                >
                  匯出 PDF 報告
                </Button>
              </Col>
            </Row>
          </Card>
        </>
      )}

      {currentScan && currentScan.status === 'failed' && (
        <Card style={{ marginBottom: 24 }}>
          <Empty description={
            <span>掃描失敗：{currentScan.error_message || '未知錯誤'}</span>
          } />
        </Card>
      )}

      {/* Scan History */}
      <Card title={<Space>掃描紀錄 <Button size="small" icon={<ReloadOutlined />} onClick={fetchHistory} /></Space>}>
        <Table
          columns={historyColumns}
          dataSource={history}
          rowKey="id"
          loading={loadingHistory}
          size="small"
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: '尚無掃描紀錄' }}
          onRow={(r) => ({ onClick: () => handleView(r.id), style: { cursor: 'pointer' } })}
        />
      </Card>

      {/* Subscription Gate Modal */}
      <Modal
        title="需要訂閱或優惠券"
        open={subGateOpen}
        onCancel={() => setSubGateOpen(false)}
        footer={null}
        width={420}
      >
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <RocketOutlined style={{ fontSize: 48, color: '#fa8c16', marginBottom: 12 }} />
          <Title level={4}>需要有效訂閱或優惠券才能使用 AEO Scanner</Title>
          <Paragraph type="secondary">
            AEO Scanner 使用 AI 模型（Claude / DeepSeek）分析網站，屬於進階功能。
          </Paragraph>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Button type="primary" size="large" icon={<DollarOutlined />} block
              onClick={() => { setSubGateOpen(false); navigate('/billing'); }}>
              前往訂閱方案
            </Button>
            <Button size="large" icon={<GiftOutlined />} block
              onClick={() => { setSubGateOpen(false); navigate('/billing'); }}>
              兌換優惠券
            </Button>
            <Button size="large" icon={<SendOutlined />} block
              onClick={() => { setSubGateOpen(false); navigate('/coupon-request'); }}>
              申請免費優惠券
            </Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
}
